import { useEffect } from 'react';
import Script from 'next/script';
import appConfig from 'app-config';
import { ONETRUST_COOKIE_CATEGORY, addConsentCallback, isCookieCategoryConsented } from 'src/utils/onetrust';

/** AppsFlyer SDK가 내부적으로 사용하는 쿠키 목록. consent 철회 시 삭제 대상 */
const APPSFLYER_COOKIE_NAMES = ['afUserId', 'af_id', 'AF_SYNC'];

/**
 * AppsFlyer SDK 메모리(`afWebUserId`)를 초기화하고 관련 쿠키를 삭제한다.
 *
 * @remarks
 * AppsFlyer SDK 내부의 `beforeunload` 핸들러가 메모리의 `afWebUserId`로 `afUserId` 쿠키를 재생성하므로,
 * 쿠키 삭제와 함께 메모리도 반드시 초기화해야 한다.
 */
const cleanupAppsflyerSDK = () => {
  const afSdk = window.AF_SDK;
  if (!afSdk) return;
  if (afSdk?.PLUGINS?.PBA) {
    afSdk.PLUGINS.PBA.afWebUserId = null;
  }
  const coverDomain: string = afSdk?.PLUGINS?.PBA?.coverDomain || '';
  APPSFLYER_COOKIE_NAMES.forEach(name => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    if (coverDomain) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${coverDomain}`;
    }
  });
};

/**
 * AppsFlyer PBA SDK를 로드하고, OneTrust consent 변경에 따라 쿠키를 정리하는 컴포넌트.
 *
 * @remarks
 * - OneTrust autoBlock.js와 연동하여 Targeting/Advertising 쿠키 consent가 있을 때만 SDK가 실행된다.
 * - consent 철회 시 SDK 메모리와 쿠키를 모두 정리한다.
 */
const AppsflyerPBAThirdPartyScript = () => {
  useEffect(() => {
    addConsentCallback(() => {
      if (!isCookieCategoryConsented(ONETRUST_COOKIE_CATEGORY.TARGETING)) cleanupAppsflyerSDK();
    });
  }, []);
  if (!appConfig.appsflyerWebDevKey) {
    return null;
  }
  return <Script id='appsflyer-pba-script'
  /**
   * type="text/plain"으로 선언하면 OneTrust autoBlock.js가 consent 동의 후 "type="text/javascript"로 변경하여 실행한다.
   * @see https://my.onetrust.com/s/article/UUID-518074a1-a6da-81c3-be52-bae7685d9c94#idm2832
   */ type='text/plain' className={`optanon-category-${ONETRUST_COOKIE_CATEGORY.TARGETING}`} strategy='afterInteractive' dangerouslySetInnerHTML={{
    /**
     * Appsflyer PBA SDK는 script형태로 제공된다.
     * @see https://support.appsflyer.com/hc/en-us/articles/360001610038-PBA-Web-SDK-integration-guide
     */
    __html: `
          !function(t,e,n,s,a,c,i,o,p){t.AppsFlyerSdkObject=a,t.AF=t.AF||function(){
          (t.AF.q=t.AF.q||[]).push([Date.now()].concat(Array.prototype.slice.call(arguments)))},
          t.AF.id=t.AF.id||i,t.AF.plugins={},o=e.createElement(n),p=e.getElementsByTagName(n)[0],o.async=1,
          o.src="https://websdk.appsflyer.com?"+(c.length>0?"st="+c.split(",").sort().join(",")+"&":"")+(i.length>0?"af_id="+i:""),
          p.parentNode.insertBefore(o,p)}(window,document,"script",0,"AF","pba",{pba: {webAppId: "${appConfig.appsflyerWebDevKey}"}})
        `
  }} data-sentry-element="Script" data-sentry-component="AppsflyerPBAThirdPartyScript" data-sentry-source-file="index.tsx" />;
};
export default AppsflyerPBAThirdPartyScript;